/**
 * OrderDashboard Component
 *
 * This component provides an order management dashboard for franchise users.
 * It displays orders grouped by date in a card-based layout with filtering capabilities.
 *
 * Features:
 * - Implementing pagination feature throughout order dashboard
 * - Date-wise grouping of orders
 * - Search functionality
 * - Loading and error states
 * - Responsive design
 * - Orders sorted by most recent date first
 * - Expand/Collapse functionality for date groups
 * - Sorting functionality for various columns
 * - Month wise or custom range export feature
 * - Filter toggle which includes status wise, date wise, terminal wise and page limit wise
 */
import type React from "react"
import { useState, useEffect } from "react"
import { container } from "../../infrastructure/container"
import type { User } from "../../infrastructure/models/user"
import type { Logger } from "../../infrastructure/services/logger"
import type { SessionService } from "../../infrastructure/services/session"
import type { ApiService } from "../../infrastructure/services/api"
import { useNavigate } from "react-router-dom"
import { useSearch } from "../components/ProtectedLayout"
import type { GetOrderResponse } from "models/response/GetOrdersResponse"
import { ClipLoader } from "react-spinners"
import { COLORS } from "../constants/colors"
import type { Terminal } from "../../infrastructure/models/user"
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns"
import * as XLSX from "xlsx"
import { FranchiseRole } from "../../infrastructure/models/enums/franchiserole"
import { Plus, Minus, Filter, ShoppingBag, IndianRupee, Download, Calendar } from "lucide-react"
import { Box } from "@mui/material"
import Tooltip from "@mui/material/Tooltip"
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import ExpandMoreIcon from "@mui/icons-material/UnfoldMore"
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore"
import { convertUTCtoIST, getISTDateString, formatISTTime, formatISTDateTime, formatISTDateDisplay } from "../../infrastructure/utils/time"

const OrderDashboard: React.FC = () => {
  // Dependency injection using container
  const sessionService = container.resolve<SessionService>("sessionService")
  const apiService = container.resolve<ApiService>("apiService")
  const logger = container.resolve<Logger>("logger")

  // Context hooks
  const { searchQuery } = useSearch()
  const navigate = useNavigate()

  // State management
  const [user, setUser] = useState<User | null>(null)
  const [orders, setOrders] = useState<GetOrderResponse[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [selectedOrder, setSelectedOrder] = useState<GetOrderResponse | null>(null)
  const [showOrderPopup, setShowOrderPopup] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState<boolean>(false)
  const [selectedStatus, setSelectedStatus] = useState<string>("all")
  const [terminals, setTerminals] = useState<Terminal[]>([])
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [selectedTerminal, setSelectedTerminal] = useState<string>("all")
  const [showFilterDropdown, setShowFilterDropdown] = useState<boolean>(false)
  const [showExportDropdown, setShowExportDropdown] = useState<boolean>(false)
  const [exportOption, setExportOption] = useState<string>("")
  const [exportFromDate, setExportFromDate] = useState<Date | null>(null)
  const [exportToDate, setExportToDate] = useState<Date | null>(null)
  const [expandedDate, setExpandedDate] = useState<string | null>(null)
  const [expandedAll, setExpandedAll] = useState<boolean>(false)
  const [sortConfig, setSortConfig] = useState<{
    key: string
    direction: "asc" | "desc"
  } | null>(null)

  const [currentPage, setCurrentPage] = useState<number>(1)
  const [totalOrders, setTotalOrders] = useState<number>(0)
  const [totalRevenue, setTotalRevenue] = useState<number>(0)
  const [totalRevenueAfterDeduction, setTotalRevenueAfterDeduction] = useState<number>(0)
  const [limit, setLimit] = useState<number>(350)
  const [month, setMonth] = useState<string>("")
  const [statusCounts, setStatusCounts] = useState<any>([])

  // Status priority for sorting
  const statusPriority = {
    "in-progress": 0,
    pending: 1,
    completed: 2,
    canceled: 3,
    "pickup-locker-pending": 4,
  }
  const sizePriority = {
    small: 0,
    medium: 1,
    large: 2,
    "x-large": 3,
    custom: 4,
  }
  const normalizeStatus = (status: string) => {
    return status.toLowerCase().replace(/-/g, "")
  }

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc"
    if (sortConfig && sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
  }

  const clearDateFilter = () => {
    setSelectedDate(null)
  }

  const clearAllFilters = () => {
    setSelectedStatus("all")
    setSelectedDate(null)
    setSelectedTerminal("all")
  }

  const toggleTableExpand = (date: string) => {
    setExpandedDate((prev) => (prev === date ? null : date))
  }

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit)
    setCurrentPage(1) // Reset to first page when changing limit
  }

  /**
   * Filtering logic
   */
  const filteredOrders = orders.filter((order) => {
    const terminalName = terminals.find((t) => t.terminalId === order.terminal)?.name || ""
    const orderDateStr = order.dropOffTime ? getISTDateString(order.dropOffTime) : null

    // Search across multiple relevant fields
    const matchesSearch =
      terminalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.status?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.receiverMobile?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      order.lockerSize?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesStatus = selectedStatus === "all" || order.status?.toLowerCase() === selectedStatus.toLowerCase()

    const matchesTerminal = selectedTerminal === "all" || order.terminal === selectedTerminal

    const matchesDate =
      !selectedDate || (orderDateStr && new Date(orderDateStr).toDateString() === selectedDate.toDateString())

    return matchesSearch && matchesStatus && matchesTerminal && matchesDate
  })

  const fetchData = async () => {
    try {
      setIsProcessing(true)
      setIsLoading(true)

      const storedUserDetails = sessionService.user
      if (!storedUserDetails) throw new Error("No user data found in session")

      setUser(storedUserDetails)

      const franchiseId = storedUserDetails.franchise?.toString()
      if (!franchiseId) throw new Error("Franchise ID not available")

      const sessionTerminals = sessionService.terminals || []
      setTerminals(sessionTerminals)

      // Pass page & limit explicitly here
      const page = currentPage
      const limitValue = limit
      const { orders: ordersData, total, totalRevenue, totalRevenueAfterDeduction, month } = await apiService.getOrderDetails(franchiseId, page, limitValue)

      if (!ordersData || !Array.isArray(ordersData)) {
        throw new Error("Invalid response format: expected array of orders")
      }

      setOrders(ordersData)
      setTotalOrders(total)
      setTotalRevenue(totalRevenue)
      setTotalRevenueAfterDeduction(totalRevenueAfterDeduction ?? 0)
      setMonth(month)

      setTimeout(() => {
        console.log("Orders state after update:", orders.length)
      }, 0)
    } catch (error: any) {
      console.error("Detailed error in fetchData:", error)
      logger.error("Error fetching order data:", error)

      let errorMessage = "Failed to load orders"
      if (error instanceof TypeError && error.message.includes("fetch")) {
        errorMessage = "Network error: Unable to connect to API. Please check if the server is running."
      } else if (error.message.includes("JSON")) {
        errorMessage = "API returned invalid response format. Please check the API endpoint."
      } else if (error.message.includes("HTTP error")) {
        errorMessage = `API error: ${error.message}. Please check the API endpoint and permissions.`
      } else if (error.message.includes("Franchise Id is required")) {
        errorMessage = "Franchise ID is missing. Please check your session data."
      }

      setError(errorMessage)
    } finally {
      setIsLoading(false)
      setIsProcessing(false)
    }
  }

  useEffect(() => {
    const init = async () => {
      setIsLoading(true)
      try {
        const storedUserDetails = sessionService.user
        if (!storedUserDetails) throw new Error("No user data found in session")

        setUser(storedUserDetails)

        const franchiseId = storedUserDetails.franchise?.toString()
        if (!franchiseId) throw new Error("Franchise ID not available")

        const sessionTerminals = sessionService.terminals || []
        setTerminals(sessionTerminals)

        console.log("Using API service to fetch orders for franchise:", franchiseId)

        const page = currentPage
        const {
          orders: ordersData,
          total,
          totalRevenue,
          totalRevenueAfterDeduction,
          statusCounts,
          month
        } = await apiService.getOrderDetails(franchiseId, page, limit)

        console.log("Raw API response:", { ordersData, total, page, limit })

        if (!ordersData || !Array.isArray(ordersData)) {
          throw new Error("Invalid response format: expected array of orders")
        }

        setOrders(ordersData)
        setTotalOrders(total ?? 0)
        setTotalRevenue(totalRevenue ?? 0)
        setTotalRevenueAfterDeduction(totalRevenueAfterDeduction ?? 0)
        setStatusCounts(statusCounts ?? [])
        setMonth(month)
      } catch (error: any) {
        console.error("Detailed error in fetchData:", error)
        logger.error("Error fetching order data:", error)

        let errorMessage = "Failed to load orders"
        if (error instanceof TypeError && error.message.includes("fetch")) {
          errorMessage = "Network error: Unable to connect to API. Please check if the server is running."
        } else if (error.message?.includes?.("JSON")) {
          errorMessage = "API returned invalid response format. Please check the API endpoint."
        } else if (error.message?.includes?.("HTTP error")) {
          errorMessage = `API error: ${error.message}. Please check the API endpoint and permissions.`
        } else if (error.message?.includes?.("Franchise Id is required")) {
          errorMessage = "Franchise ID is missing. Please check your session data."
        }

        setError(errorMessage)
      } finally {
        setIsLoading(false)
      }
    }

    void init()
  }, [currentPage, limit])

  const groupedOrders = filteredOrders.reduce(
    (acc, order) => {
      // Convert UTC dropOffTime to IST date string for grouping
      const key = order.dropOffTime ? getISTDateString(order.dropOffTime) : "no-date"

      if (!acc[key]) {
        acc[key] = {
          // Store the IST date for sorting and display
          date: order.dropOffTime ? convertUTCtoIST(order.dropOffTime) : null,
          orders: [],
        }
      }
      acc[key].orders.push(order)
      return acc
    },
    {} as Record<string, { date: Date | null; orders: GetOrderResponse[] }>,
  )

  const currentDate = new Date()
  const currentMonth = currentDate.getMonth()
  const currentYear = currentDate.getFullYear()

  const previousMonth = new Date(currentYear, currentMonth - 1, 1)
  const twoMonthsAgo = new Date(currentYear, currentMonth - 2, 1)

  const previousMonthName = previousMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  const twoMonthsAgoName = twoMonthsAgo.toLocaleDateString("en-US", { month: "long", year: "numeric" })

  const calculateNetAmount = (payments = []) => {
    return payments.reduce(
      (totals, p) => {
        const status = p.status?.toUpperCase()

        // Approved payment
        if (
          !p.refundOperationId &&
          (status === "COMPLETED" || status === "APPROVED")
        ) {
          totals.approved += p.amount
        }

        // Refunded payment
        if (
          p.refundOperationId &&
          (status === "COMPLETED" || status === "PENDING")
        ) {
          totals.refunded += p.amount
        }

        return totals
      },
      { approved: 0, refunded: 0 }
    )
  }

  const handleExportApply = async () => {
    if (!exportOption) {
      alert("Please select an export option")
      return
    }

    try {
      let fromDate = ""
      let toDate = ""

      const currentDate = new Date()
      const currentYear = currentDate.getFullYear()
      const currentMonth = currentDate.getMonth()

      const formatDateSafe = (date: Date) => {
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, "0")
        const day = String(date.getDate()).padStart(2, "0")
        return `${year}-${month}-${day}`
      }

      if (exportOption === "twoMonthsAgo") {
        let targetYear = currentYear
        let targetMonth = currentMonth - 2
        if (targetMonth < 0) { targetYear = currentYear - 1; targetMonth += 12 }
        fromDate = formatDateSafe(new Date(targetYear, targetMonth, 1))
        toDate = formatDateSafe(new Date(targetYear, targetMonth + 1, 0))
      } else if (exportOption === "previousMonth") {
        let targetYear = currentYear
        let targetMonth = currentMonth - 1
        if (targetMonth < 0) { targetYear = currentYear - 1; targetMonth += 12 }
        fromDate = formatDateSafe(new Date(targetYear, targetMonth, 1))
        toDate = formatDateSafe(new Date(targetYear, targetMonth + 1, 0))
      } else if (exportOption === "custom" && exportFromDate && exportToDate) {
        fromDate = formatDateSafe(exportFromDate)
        toDate = formatDateSafe(exportToDate)
      }

      const storedUserDetails = sessionService.user
      const franchiseId = storedUserDetails?.franchise?.toString()
      if (!franchiseId) { console.error("Franchise ID not found"); return }

      const { orders: ordersData, deductionPercent } = await apiService.downloadOrders(franchiseId, fromDate, toDate)

      generateExcelFile(ordersData, deductionPercent)

      setShowExportDropdown(false)
      clearExportFilters()
    } catch (error) {
      console.error("Export failed:", error)
    }
  }

  const generateExcelFile = (orders: GetOrderResponse[], deductionPercent: number) => {
    let grandApproved = 0
    let grandRefunded = 0

    const rows = orders.map((order) => {
      const maskedPhone =
        order.receiverMobile?.slice(-4).padStart(order.receiverMobile.length, "*") || ""

      let approvedAmount = 0
      let refundedAmount = 0

      const payments = order.payments ?? []

      // Build a set of transactionIds that appear more than once
      // (duplicate transactionId = the second entry is a refund)
      const txnIdCount: Record<string, number> = {}
      payments.forEach((p) => {
        if (p.transactionId) {
          txnIdCount[p.transactionId] = (txnIdCount[p.transactionId] || 0) + 1
        }
      })

      // Track which transactionIds have already been counted as approved
      const approvedTxnIds = new Set<string>()

      payments.forEach((p) => {
        const status = (p.status || "").toUpperCase()
        const txnId = p.transactionId

        // Has refundOperationId → definite refund (when backend sends it)
        const hasRefundId = !!(
          p.refundOperationId !== undefined &&
          p.refundOperationId !== null &&
          p.refundOperationId !== ""
        )

        // Duplicate transactionId → second occurrence is a refund
        const isDuplicateTxn =
          txnId &&
          txnIdCount[txnId] > 1 &&
          approvedTxnIds.has(txnId)

        const isRefund = hasRefundId || isDuplicateTxn

        if (
          !isRefund &&
          txnId &&
          (status === "COMPLETED" || status === "APPROVED")
        ) {
          approvedAmount += Number(p.amount) || 0
          approvedTxnIds.add(txnId) // mark this txnId as seen/approved
        } else if (
          !isRefund &&
          !txnId &&
          (status === "COMPLETED" || status === "APPROVED")
        ) {
          // No transactionId at all — count as approved (e.g. box pay first payment)
          approvedAmount += Number(p.amount) || 0
        }

        if (isRefund && (status === "COMPLETED" || status === "PENDING")) {
          refundedAmount += Number(p.amount) || 0
        }
      })

      const netAmount = approvedAmount - refundedAmount
      grandApproved += approvedAmount
      grandRefunded += refundedAmount

      return [
        order.orderId,
        order.dropOffTime ? formatISTDateTime(order.dropOffTime) : "-",
        maskedPhone,
        order.status,
        order.lockerSize,
        `₹${approvedAmount.toFixed(2)}`,
        `₹${refundedAmount.toFixed(2)}`,
        `₹${netAmount.toFixed(2)}`,
      ]
    })

    const grandRevenue = grandApproved - grandRefunded
    const deducted = Math.ceil((grandRevenue * deductionPercent) / 100)
    const grandNet = grandRevenue - deducted

    const excelData = [
      [
        "Order ID", "Date", "Customer Phone", "Status", "Locker Size",
        "Approved Payments", "Refunded Payments", "Net Amount",
      ],
      ...rows,
      [],
      ["Grand Total", "", "", "", "",
        `₹${grandApproved.toFixed(2)}`,
        `₹${grandRefunded.toFixed(2)}`,
        `₹${grandRevenue.toFixed(2)}`
      ],
      ["Deducted Amount", "", "", "", "", "", "", `₹${deducted.toFixed(2)}`],
      ["Net Revenue (After Deduction)", "", "", "", "", "", "", `₹${grandNet.toFixed(2)}`],
    ]

    const ws = XLSX.utils.aoa_to_sheet(excelData)
    const wb = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(wb, ws, "Orders Report")
    XLSX.writeFile(wb, `orders_report_${new Date().toISOString().split("T")[0]}.xlsx`)
  }

  const clearExportFilters = () => {
    setExportOption("")
    setExportFromDate(null)
    setExportToDate(null)
  }

  // Sort groups by date (newest first)
  const sortedGroups = Object.entries(groupedOrders)
    .sort(([aKey, aVal], [bKey, bVal]) => {
      if (!aVal.date) return 1
      if (!bVal.date) return -1
      return bVal.date.getTime() - aVal.date.getTime()
    })
    .map(([key, value]) => ({
      dateKey: key,
      displayDate: value.date ? formatISTDateDisplay(value.date) : "No Date",
      ...value,
    }))

  const totalPages = Math.ceil(totalOrders / limit)
  const handlePageChange = (page: number) => {
    const next = Math.max(1, Math.min(totalPages || 1, page))
    setCurrentPage(next)
  }

  // Loading state UI
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-200">
        <ClipLoader size={50} color={COLORS.BLUE} />
        <span className="ml-4 text-blue-500">Loading orders...</span>
      </div>
    )
  }

  // Error state UI
  if (error) {
    return (
      <div className="text-center p-10">
        <div className="text-red-600 mb-4">{error}</div>
        <button onClick={fetchData} className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600">
          Retry
        </button>
      </div>
    )
  }

  // No user data state
  if (!user) {
    return <div className="text-center p-10">No user data available.</div>
  }
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Export Period</label>
    <div className="space-y-2">
      <label className="flex items-center">
        <input
          type="radio"
          name="exportOption"
          value="twoMonthsAgo"
          checked={exportOption === "twoMonthsAgo"}
          onChange={(e) => setExportOption(e.target.value)}
          className="mr-2"
        />
        <span className="text-sm font-medium">{twoMonthsAgoName}</span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name="exportOption"
          value="previousMonth"
          checked={exportOption === "previousMonth"}
          onChange={(e) => setExportOption(e.target.value)}
          className="mr-2"
        />
        <span className="text-sm font-medium">{previousMonthName} (Previous Month)</span>
      </label>
      <label className="flex items-center">
        <input
          type="radio"
          name="exportOption"
          value="custom"
          checked={exportOption === "custom"}
          onChange={(e) => setExportOption(e.target.value)}
          className="mr-2"
        />
        <span className="text-sm font-medium">Custom Date Range</span>
      </label>
    </div>
  </div>

  return (
    <div className="pt-0 mt-[-8px] px-2 pb-6 w-full max-w-full">
      {/* Processing Overlay */}
      {isProcessing && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center">
            <ClipLoader size={50} color={COLORS.BLUE} />
            <span className="mt-4 text-blue-500 font-medium">Loading order data...</span>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="mb-4">
        {/* Top Row: Heading */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-2 gap-2">
          {/* Heading */}
          <h2 className="text-2xl font-semibold text-gray-800">Order Management</h2>
        </div>

        {/* Second Row: Totals + Actions */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2 mt-1 w-full">
          {/* Left-aligned items */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Totals */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Current Month */}
              <div className="bg-purple-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-purple-100">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <span className="text-sm text-purple-600 font-medium block">
                    Current Month
                  </span>
                  <div className="text-sm font-bold text-purple-800">
                    {month || ""}
                  </div>
                </div>
              </div>
              {/* Total Orders */}
              <div className="bg-blue-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-blue-100">
                <div className="p-2 bg-blue-100 rounded-full">
                  <ShoppingBag className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <span className="text-sm text-blue-600 font-medium block">Orders (Current Month)</span>
                  <div className="text-xl font-bold text-blue-800">{totalOrders || 0}</div>
                </div>
              </div>
              {user?.role !== FranchiseRole.USER && (
                <div className="relative bg-green-50 px-4 py-3 rounded-lg flex items-center gap-3 border border-green-100">
                  {/* Info Icon Top Right */}
                  <Tooltip title="Total revenue after PhonePe commision deduction" arrow>
                    <InfoOutlinedIcon
                      fontSize="small"
                      className="absolute top-2 right-2 text-green-600 cursor-pointer"
                    />
                  </Tooltip>
                  <div className="p-2 bg-green-100 rounded-full">
                    <IndianRupee className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <span className="text-sm text-green-600 font-medium block">Revenue (Current Month)</span>
                    <div className="text-xl font-bold text-green-800">{totalRevenueAfterDeduction || 0}</div>
                  </div>
                </div>
              )}
            </div>

            {/* Active Filters Display */}
            {(selectedStatus !== "all" || selectedDate || selectedTerminal !== "all") && (
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-sm text-gray-600 font-sm">Active filters:</span>

                {/* Status Filter Chip */}
                {selectedStatus !== "all" && (
                  <span className="flex items-center gap-1 px-1 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium">
                    Status: {selectedStatus}
                    <button
                      onClick={() => setSelectedStatus("all")}
                      className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                    >
                      ✕
                    </button>
                  </span>
                )}

                {/* Terminal Filter Chip */}
                {selectedTerminal !== "all" && (
                  <span className="flex items-center gap-1 px-1 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                    Terminal: {terminals.find((t) => t.terminalId === selectedTerminal)?.name}
                    <button
                      onClick={() => setSelectedTerminal("all")}
                      className="ml-1 text-purple-600 hover:text-purple-800 font-bold"
                    >
                      ✕
                    </button>
                  </span>
                )}

                {/* Date Filter Chip */}
                {selectedDate && (
                  <span className="flex items-center gap-1 px-1 py-1 bg-orange-100 text-orange-800 rounded text-sm font-medium">
                    Date: {selectedDate.toLocaleDateString()}
                    <button
                      onClick={() => setSelectedDate(null)}
                      className="ml-1 text-orange-600 hover:text-orange-800 font-bold"
                    >
                      ✕
                    </button>
                  </span>
                )}

                {/* Clear All Chip */}
                <button
                  onClick={clearAllFilters}
                  className="px-1 py-1 bg-gray-200 text-gray-700 rounded text-sm font-medium hover:bg-gray-300"
                >
                  Clear All ✕
                </button>
              </div>
            )}
          </div>

          {/* Right-aligned items */}
          <div className="flex items-center gap-2 relative">
            {/* Filter Button */}
            <div className="relative">
              <button
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:border-blue-500 font-medium text-sm transition-colors"
              >
                <Filter size={20} />
                <svg className="fill-current h-5 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                  <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                </svg>
              </button>

              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                  <div className="p-4 space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-lg font-semibold text-gray-800">Filters</h3>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="text-gray-500 hover:text-gray-700 font-medium"
                      >
                        ✕
                      </button>
                    </div>

                    {/* Status Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium"
                      >
                        <option value="all">All Status</option>
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="completed">Completed</option>
                        <option value="canceled">Canceled</option>
                        <option value="pickup-locker-pending">Pickup Locker Pending</option>
                      </select>
                    </div>

                    {/* Terminal Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Terminal</label>
                      <select
                        value={selectedTerminal}
                        onChange={(e) => setSelectedTerminal(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium"
                      >
                        <option value="all">All Terminals</option>
                        {terminals.map((terminal) => (
                          <option key={terminal.terminalId} value={terminal.terminalId}>
                            {terminal.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Date Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Date</label>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={selectedDate}
                          onChange={(newValue) => setSelectedDate(newValue)}
                          disableFuture
                          slotProps={{
                            textField: {
                              size: "small",
                              sx: {
                                width: "100%",
                                backgroundColor: "white",
                                "& .MuiInputBase-input": {
                                  fontSize: "0.875rem",
                                  fontWeight: 500,
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>

                    {/* Limit Filter */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Limit</label>
                      <select
                        value={limit}
                        onChange={(e) => handleLimitChange(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 text-sm font-medium"
                      >
                        <option value={350}>350</option>
                        <option value={500}>500</option>
                        <option value={1000}>1000</option>
                        <option value={1500}>1500</option>
                        <option value={2000}>2000</option>
                      </select>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex gap-2 pt-2">
                      <button
                        onClick={clearAllFilters}
                        className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
                      >
                        Clear All
                      </button>
                      <button
                        onClick={() => setShowFilterDropdown(false)}
                        className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium transition-colors"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Expand/Collapse All Button */}
            <button
              onClick={() => {
                setExpandedAll((prev) => !prev)
                setExpandedDate(null)
              }}
              className="px-3 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 focus:outline-none focus:border-blue-500 font-medium text-sm transition-colors"
            >
              {expandedAll ? "Collapse All" : "Expand All"}
            </button>

            {/* Download Button */}
            {user?.role !== FranchiseRole.USER && (
              <div className="relative">
                <button
                  onClick={() => setShowExportDropdown(!showExportDropdown)}
                  className="flex items-center gap-2 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium text-sm transition-colors"
                >
                  <Download size={16} />
                  <span>Export</span>
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </button>

                {/* Export Dropdown */}
                {showExportDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    <div className="p-4 space-y-4">
                      <div className="flex justify-between items-center">
                        <h3 className="text-lg font-semibold text-gray-800">Export Options</h3>
                        <button
                          onClick={() => setShowExportDropdown(false)}
                          className="text-gray-500 hover:text-gray-700 font-medium"
                        >
                          ✕
                        </button>
                      </div>

                      {/* Export Period Selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Export Period</label>
                        <div className="space-y-2">
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="exportOption"
                              value="twoMonthsAgo"
                              checked={exportOption === "twoMonthsAgo"}
                              onChange={(e) => setExportOption(e.target.value)}
                              className="mr-2"
                            />
                            <span className="text-sm font-medium">{twoMonthsAgoName}</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="exportOption"
                              value="previousMonth"
                              checked={exportOption === "previousMonth"}
                              onChange={(e) => setExportOption(e.target.value)}
                              className="mr-2"
                            />
                            <span className="text-sm font-medium">{previousMonthName} (Previous Month)</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="exportOption"
                              value="custom"
                              checked={exportOption === "custom"}
                              onChange={(e) => setExportOption(e.target.value)}
                              className="mr-2"
                            />
                            <span className="text-sm font-medium">Custom Date Range</span>
                          </label>
                        </div>
                      </div>

                      {/* Custom Date Range */}
                      {exportOption === "custom" && (
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">From Date</label>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={exportFromDate}
                                onChange={(newValue) => setExportFromDate(newValue)}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: {
                                      width: "100%",
                                      backgroundColor: "white",
                                      "& .MuiInputBase-input": {
                                        fontSize: "0.875rem",
                                        fontWeight: 500,
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">To Date</label>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                              <DatePicker
                                value={exportToDate}
                                onChange={(newValue) => setExportToDate(newValue)}
                                maxDate={new Date()}
                                slotProps={{
                                  textField: {
                                    size: "small",
                                    sx: {
                                      width: "100%",
                                      backgroundColor: "white",
                                      "& .MuiInputBase-input": {
                                        fontSize: "0.875rem",
                                        fontWeight: 500,
                                      },
                                    },
                                  },
                                }}
                              />
                            </LocalizationProvider>
                          </div>
                        </div>
                      )}

                      {/* Export Actions */}
                      <div className="flex gap-2 pt-2">
                        <button
                          onClick={clearExportFilters}
                          className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm font-medium transition-colors"
                        >
                          Clear All
                        </button>
                        <button
                          onClick={handleExportApply}
                          className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm font-medium transition-colors"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Backdrop to close filter */}
      {(showFilterDropdown || showExportDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => {
            setShowFilterDropdown(false)
            setShowExportDropdown(false)
          }}
        />
      )}

      {/* Table Content */}
      <div className="mt-6 -mt-3 overflow-y-auto" style={{ maxHeight: "calc(98vh - 200px)" }}>
        {filteredOrders.length === 0 ? (
          <div className="text-center p-10">
            {searchQuery.trim() ? (
              <p className="text-gray-500 mb-4 font-medium">
                No orders found for "<strong>{searchQuery}</strong>"
              </p>
            ) : (
              <>
                <p className="text-gray-500 mb-4 font-medium">No orders found for this filter</p>
                <button
                  onClick={fetchData}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium text-sm transition-colors"
                >
                  Refresh Orders
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3 pb-4">
            {sortedGroups.map((group) => (
              <div key={group.dateKey} className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-2 bg-gray-50 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                  {/* Left: Date */}
                  <h3 className="text-base font-semibold text-gray-900">{group.displayDate}</h3>

                  {/* Center: Status Summary */}
                  <div className="flex flex-wrap items-center text-sm font-medium gap-x-3">
                    {(() => {
                      const groupDateString = group.date ? group.date.toISOString().split("T")[0] : null
                      const statusCount = statusCounts?.find((sc) => sc.date === groupDateString)
                      console.log(
                        "[v0] Debug - group.date formatted:",
                        groupDateString,
                        "statusCount found:",
                        statusCount,
                      )
                      console.log("[v0] Debug - all statusCounts:", statusCounts)
                      return (
                        <>
                          <span>Total Orders: {statusCount?.totalCount || group.orders.length}</span>
                          <span className="text-gray-400">|</span>

                          <span className="text-green-600">Completed: {statusCount?.counts?.completed || 0}</span>
                          <span className="text-gray-400">|</span>

                          <span className="text-yellow-600">
                            In-Progress: {statusCount?.counts?.["in-progress"] || 0}
                          </span>
                          <span className="text-gray-400">|</span>

                          <span className="text-red-600">Pending: {statusCount?.counts?.pending || 0}</span>
                          <span className="text-gray-400">|</span>

                          <span className="text-gray-600">Canceled: {statusCount?.counts?.canceled || 0}</span>
                          <span className="text-gray-400">|</span>

                          <span className="text-orange-600">
                            Pickup-Locker-Pending: {statusCount?.counts?.["pickup-locker-pending"] || 0}
                          </span>
                        </>
                      )
                    })()}
                  </div>

                  {/* Right: Total Amount and Expand Button*/}
                  <div className="flex items-center gap-4">
                    {user?.role !== FranchiseRole.USER && (
                      <div className="text-sm font-semibold text-gray-900">
                        Total Amount: ₹
                        {
                          group.orders.reduce((sum, order) => {
                            // Only consider completed orders
                            if (order.status !== "completed") return sum

                            const { approvedTotal, refundedTotal } = order.payments.reduce(
                              (totals, payment) => {
                                const status = payment.status?.toUpperCase()
                                // Check for status Approved & Completed
                                if (
                                  !payment.refundOperationId &&
                                  (status === "APPROVED" || status === "COMPLETED")
                                ) {
                                  totals.approvedTotal += payment.amount
                                }
                                // Refunded (Check for payment object with 'refundOperationId' & status)
                                if (
                                  payment.refundOperationId &&
                                  (status === "PENDING" || status === "COMPLETED")
                                ) {
                                  totals.refundedTotal += payment.amount
                                }
                                return totals
                              },
                              { approvedTotal: 0, refundedTotal: 0 }
                            )
                            // Add net amount (approved - refunded) to overall sum
                            return sum + (approvedTotal - refundedTotal)
                          }, 0).toFixed(2)
                        }
                      </div>
                    )}

                    {/* Expand/Collapse Button */}
                    <Box sx={{ width: 20, textAlign: "center" }}>
                      <button
                        onClick={() => toggleTableExpand(group.dateKey)}
                        style={{
                          cursor: "pointer",
                          fontSize: "16px",
                          color: expandedDate === group.dateKey ? COLORS.INACTIVE : COLORS.ACTIVE,
                          background: "transparent",
                          border: "none",
                        }}
                        title={expandedDate === group.dateKey ? "Collapse Table" : "Expand Table"}
                      >
                        {expandedDate === group.dateKey ? <Minus size={24} /> : <Plus size={24} />}
                      </button>
                    </Box>
                  </div>
                </div>

                {(expandedDate === group.dateKey || expandedAll) && (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-300 sticky top-0">
                        <tr className="text-center">
                          <th className="px-3 py-1 whitespace-nowrap text-sm font-semibold text-gray-700">
                            PHONE NUMBER
                          </th>
                          <th className="px-3 py-1 text-sm font-semibold text-gray-700">TERMINAL</th>
                          <th
                            className="px-3 py-1 cursor-pointer text-sm font-semibold text-gray-700"
                            onClick={() => handleSort("locker")}
                          >
                            LOCKER
                            {sortConfig?.key === "locker" ? (
                              sortConfig.direction === "asc" ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            ) : (
                              <UnfoldMoreIcon />
                            )}
                          </th>
                          <th className="px-3 py-1 text-sm font-semibold text-gray-700">
                            STATUS
                            {sortConfig?.key === "status" ? (
                              sortConfig.direction === "asc" ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            ) : (
                              <UnfoldMoreIcon />
                            )}
                          </th>
                          {user?.role !== FranchiseRole.USER && (
                            <th className="px-3 py-1 text-sm font-semibold text-gray-700">LOCKER PRICE</th>
                          )}
                          <th
                            className="px-3 py-1 cursor-pointer text-sm font-semibold text-gray-700"
                            onClick={() => handleSort("size")}
                          >
                            SIZE
                            {sortConfig?.key === "size" ? (
                              sortConfig.direction === "asc" ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            ) : (
                              <UnfoldMoreIcon />
                            )}
                          </th>
                          <th
                            className="px-3 py-1 cursor-pointer text-sm font-semibold text-gray-700"
                            onClick={() => handleSort("dropOff")}
                          >
                            DROP OFF
                            {sortConfig?.key === "dropOff" ? (
                              sortConfig.direction === "asc" ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            ) : (
                              <UnfoldMoreIcon />
                            )}
                          </th>
                          <th
                            className="px-3 py-1 cursor-pointer text-sm font-semibold text-gray-700"
                            onClick={() => handleSort("pickUp")}
                          >
                            PICK UP
                            {sortConfig?.key === "pickUp" ? (
                              sortConfig.direction === "asc" ? (
                                <ExpandLessIcon />
                              ) : (
                                <ExpandMoreIcon />
                              )
                            ) : (
                              <UnfoldMoreIcon />
                            )}
                          </th>
                          {user?.role !== FranchiseRole.USER && (
                            <th className="px-3 py-1 text-sm font-semibold text-gray-700">PAYMENT</th>
                          )}
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {group.orders
                          .sort((a, b) => {
                            const dir = sortConfig?.direction === "desc" ? -1 : 1

                            if (!sortConfig) {
                              const aPriority = statusPriority[normalizeStatus(a.status.toLowerCase())] ?? 99
                              const bPriority = statusPriority[normalizeStatus(b.status.toLowerCase())] ?? 99
                              return aPriority - bPriority
                            }

                            const { key } = sortConfig

                            if (key === "locker") return dir * (a.lockerNumber - b.lockerNumber)

                            if (key === "status") {
                              const aStatus = normalizeStatus(a.status)
                              const bStatus = normalizeStatus(b.status)
                              return dir * aStatus.localeCompare(bStatus)
                            }

                            if (key === "size") {
                              const aKey = a.lockerSize.toLowerCase()
                              const bKey = b.lockerSize.toLowerCase()
                              const aPriority = sizePriority[aKey] ?? 99
                              const bPriority = sizePriority[bKey] ?? 99
                              return dir * (aPriority - bPriority)
                            }

                            if (key === "dropOff")
                              return dir * (new Date(a.dropOffTime).getTime() - new Date(b.dropOffTime).getTime())

                            if (key === "pickUp") {
                              if (!a.pickUpTime) return 1
                              if (!b.pickUpTime) return -1
                              return dir * (new Date(a.pickUpTime).getTime() - new Date(b.pickUpTime).getTime())
                            }

                            return 0
                          })
                          .map((order) => {
                            // Check if this order has any refunded payment
                            const hasRefund = order.payments?.some(
                              (p) => p.status?.toLowerCase() === "refunded"
                            )

                            return (
                              <tr
                                key={`${order.orderId}-${order.receiverMobile}`}
                                className={`hover:bg-gray-50 ${hasRefund ? "bg-yellow-100" : ""
                                  }`} // highlight refunded orders
                              >
                                <td className="px-6 py-2 whitespace-nowrap">
                                  <button
                                    onClick={() => {
                                      setSelectedOrder(order)
                                      setShowOrderPopup(true)
                                    }}
                                    className="text-blue-600 underline hover:text-blue-800 font-medium text-sm"
                                  >
                                    {order.receiverMobile || "-"}
                                  </button>
                                </td>

                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                                  {terminals.find(
                                    (t) =>
                                      t.terminalId.toString() ===
                                      order.terminal.toString()
                                  )?.name || "-"}
                                </td>

                                <td className="px-2 sm:px-6 py-2 text-center text-sm font-medium">
                                  {order.lockerNumber || "-"}
                                </td>

                                <td className="px-2 sm:px-6 py-2 text-center text-sm">
                                  {order.status ? (
                                    <span
                                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold
                                        ${order.status.toLowerCase() ===
                                          "completed"
                                          ? "bg-green-100 text-green-700"
                                          : order.status.toLowerCase() ===
                                            "in-progress"
                                            ? "bg-yellow-100 text-yellow-700"
                                            : order.status.toLowerCase() ===
                                              "pending"
                                              ? "bg-red-100 text-red-700"
                                              : order.status.toLowerCase() ===
                                                "canceled"
                                                ? "bg-gray-300 text-gray-800"
                                                : order.status.toLowerCase() ===
                                                  "pickup-locker-pending"
                                                  ? "bg-orange-100 text-orange-700"
                                                  : "bg-gray-100 text-gray-700"
                                        }`}
                                    >
                                      {order.status}
                                    </span>
                                  ) : (
                                    "-"
                                  )}
                                </td>

                                {user?.role !== FranchiseRole.USER && (
                                  <td className="px-6 py-2 text-center text-sm font-medium">
                                    {order.lockerPrice || "-"}
                                  </td>
                                )}

                                <td className="px-6 py-2 text-center text-sm font-medium">
                                  {order.lockerSize || "-"}
                                </td>

                                <td className="px-6 py-2 text-center text-sm font-medium">
                                  {order.dropOffTime
                                    ? formatISTTime(order.dropOffTime)
                                    : "-"}
                                </td>

                                <td className="px-6 py-2 text-center text-sm font-medium">
                                  {order.pickUpTime
                                    ? formatISTTime(order.pickUpTime)
                                    : "-"}
                                </td>

                                {user?.role !== FranchiseRole.USER && (
                                  <td className="px-6 py-2 text-center text-sm font-medium">
                                    {order.payments?.length ? (
                                      (() => {
                                        const { approved = 0, refunded = 0 } =
                                          calculateNetAmount(order.payments)
                                        const netAmount = approved - refunded

                                        return (
                                          <span>
                                            <span className="text-green-600 font-semibold">
                                              ₹{approved.toFixed(2)}
                                            </span>
                                            {" - "}
                                            <span className="text-red-600 font-semibold">
                                              ₹{refunded.toFixed(2)}
                                            </span>
                                            {" = "}
                                            <span className="text-blue-600 font-bold">
                                              ₹{netAmount.toFixed(2)}
                                            </span>
                                          </span>
                                        )
                                      })()
                                    ) : (
                                      "No Payments"
                                    )}
                                  </td>
                                )}
                              </tr>
                            )
                          })}
                      </tbody>
                    </table>
                    {showOrderPopup && selectedOrder && (
                      <>
                        <div className="fixed inset-0 z-40 bg-gray bg-opacity-30 flex items-center justify-center">
                          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-4xl relative max-h-[90vh] overflow-y-auto">
                            <h2 className="text-xl font-semibold mb-2">Order Details</h2>

                            <div className="grid grid-cols-2 gap-x-4 gap-y text-sm font-medium">
                              <p>
                                <strong>Order Id:</strong> {selectedOrder.orderId}
                              </p>
                              <p>
                                <strong>User Id:</strong> {selectedOrder.user}
                              </p>

                              <p>
                                <strong>Franchise:</strong> {sessionService.franchiseName}
                              </p>

                              <p>
                                <strong>Terminal Name:</strong>{" "}
                                {terminals.find((t) => t.terminalId === selectedOrder.terminal)?.name ||
                                  selectedOrder.terminal}
                              </p>

                              <p>
                                <strong>Collect Pin:</strong> {selectedOrder.collectPin}
                              </p>

                              <p>
                                <strong>Locker Number:</strong> {selectedOrder.lockerNumber}
                              </p>
                              <p>
                                <strong>Locker Size:</strong> {selectedOrder.lockerSize}
                              </p>

                              <p>
                                <strong>Locker Price:</strong> ₹{selectedOrder.lockerPrice}
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                <span
                                  className={`px-2 py-1 rounded text-white ${selectedOrder.status.toLowerCase() === "completed"
                                    ? "bg-green-600"
                                    : selectedOrder.status.toLowerCase() === "in-progress"
                                      ? "bg-yellow-600"
                                      : selectedOrder.status.toLowerCase() === "pending"
                                        ? "bg-red-600"
                                        : selectedOrder.status.toLowerCase() === "canceled"
                                          ? "bg-gray-600"
                                          : selectedOrder.status.toLowerCase() === "pickup-locker-pending"
                                            ? "bg-orange-600"
                                            : "bg-gray-500"
                                    }`}
                                >
                                  {selectedOrder.status}
                                </span>
                              </p>

                              <p>
                                <strong>Receiver Mobile:</strong> {selectedOrder.receiverMobile}
                              </p>
                              <p>
                                <strong>Drop Off:</strong> {formatISTDateTime(selectedOrder.dropOffTime)}
                              </p>

                              <p>
                                <strong>Pick Up Time:</strong>{" "}
                                {selectedOrder.pickUpTime ? formatISTDateTime(selectedOrder.pickUpTime) : "-"}
                              </p>
                              <p>
                                <strong>Created At:</strong> {formatISTDateTime(selectedOrder.createdAt)}
                              </p>

                              <p>
                                <strong>Updated At:</strong> {formatISTDateTime(selectedOrder.updatedAt)}
                              </p>
                            </div>

                            {user?.role !== FranchiseRole.USER && (
                              <div className="mt-4">
                                <h3 className="font-semibold mb-2">Payments</h3>
                                {selectedOrder.payments.length > 0 ? (
                                  <div className="space-y-4">
                                    {selectedOrder.payments.map((payment, index) => (
                                      <div
                                        key={index}
                                        className="border border-gray-800 rounded-lg p-2 shadow-sm bg-gray-300"
                                      >
                                        <p className="text-sm font-medium">
                                          <strong>Amount:</strong> ₹{payment.amount}
                                        </p>
                                        <p className="text-sm font-medium">
                                          <strong>Status:</strong> {payment.status}
                                        </p>
                                        <p className="text-sm font-medium">
                                          <strong>Transaction ID:</strong> {payment.transactionId}
                                        </p>
                                        {payment.refundOperationId && (
                                          <p className="text-sm font-medium text-red-700">
                                            <strong>Refund Operation ID:</strong>{" "}
                                            {payment.refundOperationId}
                                          </p>
                                        )}
                                        <p className="text-sm font-medium">
                                          <strong>Date:</strong> {formatISTDateTime(payment.date)}
                                        </p>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm font-medium">No payments found</p>
                                )}
                              </div>
                            )}
                            {/* Close button */}
                            <div className="flex justify-end mt-6">
                              <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm transition-colors"
                                onClick={() => setShowOrderPopup(false)}
                              >
                                Close
                              </button>
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap justify-center items-center mt-6 gap-2">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors"
          >
            Previous
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => handlePageChange(page)}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${currentPage === page ? "bg-blue-500 text-white" : "bg-gray-200 text-gray-700 hover:bg-gray-300"}`}
            >
              {page}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm transition-colors"
          >
            Next
          </button>

          {/* Items per page selector */}
          <div className="flex items-center gap-2 ml-2">
            <label htmlFor="itemsPerPage" className="text-sm text-gray-600">
              Items per page:
            </label>
            <select
              id="itemsPerPage"
              value={limit}
              onChange={(e) => {
                const value = Number(e.target.value)
                setLimit(value)
                setCurrentPage(1) // reset to first page when page size changes
              }}
              className="px-2 py-1 rounded-md border border-gray-300 bg-white text-sm text-gray-700"
            >
              <option value={350}>350</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={1500}>1500</option>
              <option value={2000}>2000</option>
            </select>
          </div>
        </div>
      )}
    </div>
  )
}

export default OrderDashboard